import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, Edit2, Search, Sparkles, Bot } from 'lucide-react';
import api from '@/lib/api';
import { formatDate } from '@/lib/utils';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import Card from '@/components/ui/Card';
import Badge from '@/components/ui/Badge';
import Pagination from '@/components/ui/Pagination';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import { SITE_NAME } from '@/config/site';

interface BlogPost {
  id: string;
  title: string;
  author: string;
  category: {
    id: string;
    name: string;
    slug: string;
  } | null;
  status: 'draft' | 'published';
  published_at: string | null;
  updated_at: string;
  ai_text_model?: string;
  is_agent_generated?: boolean;
}

interface BlogCategory {
  id: string;
  name: string;
  slug: string;
}

interface BlogResponse {
  posts: any[];
  total: number;
  page: number;
  limit: number;
}

export default function Blog() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [categories, setCategories] = useState<BlogCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: 1,
    total_count: 0,
    per_page: 20,
  });

  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || '');
  const currentPage = parseInt(searchParams.get('page') || '1', 10);

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchPosts();
  }, [currentPage, selectedCategory, searchParams]);

  const fetchCategories = async () => {
    try {
      const response = await api.get<{ categories: BlogCategory[] }>('/admin/blog/categories');
      setCategories(response.categories);
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  const fetchPosts = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      params.set('page', currentPage.toString());
      if (selectedCategory) params.set('category', selectedCategory);
      if (searchParams.get('q')) params.set('search', searchParams.get('q')!);

      const response = await api.get<BlogResponse>(`/admin/blog?${params.toString()}`);
      setPosts(response.posts.map((p: any) => ({
        id: p.id,
        title: p.title,
        author: p.author_name || `${SITE_NAME} Team`,
        category: p.category_name ? { id: p.category_id, name: p.category_name, slug: '' } : null,
        status: p.is_published ? 'published' : 'draft',
        published_at: p.published_at,
        updated_at: p.updated_at,
        ai_text_model: p.ai_text_model || undefined,
        is_agent_generated: p.is_agent_generated || false,
      })));
      setPagination({
        current_page: response.page,
        total_pages: Math.ceil(response.total / response.limit),
        total_count: response.total,
        per_page: response.limit,
      });
    } catch (err) {
      setError('Failed to load blog posts. Please try again.');
      console.error('Error fetching posts:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const params = new URLSearchParams(searchParams);
    if (searchQuery) {
      params.set('q', searchQuery);
    } else {
      params.delete('q');
    }
    params.set('page', '1');
    setSearchParams(params);
  };

  const handleCategoryChange = (value: string) => {
    setSelectedCategory(value);
    const params = new URLSearchParams(searchParams);
    if (value) {
      params.set('category', value);
    } else {
      params.delete('category');
    }
    params.set('page', '1');
    setSearchParams(params);
  };

  const handlePageChange = (page: number) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', page.toString());
    setSearchParams(params);
  };

  const categoryOptions = [
    { label: 'All Categories', value: '' },
    ...categories.map(cat => ({ label: cat.name, value: cat.slug }))
  ];

  if (loading && posts.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]" role="status" aria-live="polite">
        <LoadingSpinner />
        <span className="sr-only">Loading blog posts...</span>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Blog Posts</h1>
          <p className="text-gray-600 mt-1">Manage your blog content and articles</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="md"
            onClick={() => navigate('/admin/blog/agent')}
          >
            <Bot className="w-4 h-4 mr-2" />
            Blog Agent
          </Button>
          <Button
            variant="primary"
            size="md"
            onClick={() => navigate('/admin/blog/new')}
            aria-label="Add new blog post"
          >
            <Plus className="w-4 h-4 mr-2" aria-hidden="true" />
            Add Post
          </Button>
        </div>
      </div>

      <Card padding="lg" className="mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <form onSubmit={handleSearch} className="flex gap-2">
            <div className="flex-1">
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search posts by title..."
                aria-label="Search blog posts"
              />
            </div>
            <Button type="submit" variant="outline" aria-label="Search">
              <Search className="w-4 h-4" aria-hidden="true" />
            </Button>
          </form>

          <Select
            label="Filter by Category"
            value={selectedCategory}
            onChange={(e) => handleCategoryChange(e.target.value)}
            options={categoryOptions}
            aria-label="Filter posts by category"
          />
        </div>
      </Card>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg" role="alert">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      <Card padding="none">
        {posts.length === 0 ? (
          <div className="text-center py-12 px-4">
            <p className="text-gray-500 mb-4">
              {searchQuery || selectedCategory
                ? 'No posts found matching your criteria.'
                : 'No blog posts yet. Create your first post to get started.'}
            </p>
            {!searchQuery && !selectedCategory && (
              <Button
                variant="primary"
                onClick={() => navigate('/admin/blog/new')}
              >
                <Plus className="w-4 h-4 mr-2" aria-hidden="true" />
                Create Post
              </Button>
            )}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full" role="table" aria-label="Blog posts list">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Title
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Author
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Category
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Published
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Updated
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {posts.map((post) => (
                    <tr key={post.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-1.5">
                          <Link
                            to={`/admin/blog/${post.id}`}
                            className="text-blue-600 hover:text-blue-800 font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded"
                          >
                            {post.title}
                          </Link>
                          {post.is_agent_generated && (
                            <span title="Generated by Blog Agent" className="inline-flex items-center">
                              <Bot className="w-3.5 h-3.5 text-blue-500" />
                            </span>
                          )}
                          {post.ai_text_model && !post.is_agent_generated && (
                            <span title={`AI: ${post.ai_text_model}`} className="inline-flex items-center">
                              <Sparkles className="w-3.5 h-3.5 text-purple-400" />
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {post.author}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {post.category ? post.category.name : 'Uncategorized'}
                      </td>
                      <td className="px-6 py-4">
                        <Badge variant={post.status === 'published' ? 'green' : 'gray'}>
                          {post.status === 'published' ? 'Published' : 'Draft'}
                        </Badge>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {post.published_at ? formatDate(post.published_at) : '-'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {formatDate(post.updated_at)}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/admin/blog/${post.id}`)}
                          aria-label={`Edit ${post.title}`}
                        >
                          <Edit2 className="w-4 h-4" aria-hidden="true" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {pagination.total_pages > 1 && (
              <div className="px-6 py-4 border-t border-gray-200">
                <Pagination
                  currentPage={pagination.current_page}
                  totalPages={pagination.total_pages}
                  onPageChange={handlePageChange}
                />
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
}
